import { useMemo, useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { useAdminCatalog } from '../contexts/AdminCatalogContext';
import { useAdminDialog } from '../contexts/AdminDialogContext';
import { apiUrl } from '../lib/api';
import { getCidades, getExperiencias, getTarifarios, getTarifarioGlobalConfig, getCidadeByNome, type ExperienciaAdmin } from '../lib/experienciasStorage';
import { getGuias, guiaApto } from '../lib/guiasAgenciasStorage';
import {
  addExcursaoToStorage,
  getExcursoesFromStorage as getSharedExcursoesFromStorage,
  saveExcursoesToStorage as saveSharedExcursoesToStorage,
  type Excursao,
} from '../../../shared/lib/excursaoStorage';
import { addNotification } from '../../../shared/lib/notificationStorage';

type DayConfig = {
  city: string;
  horaSaida: string;
  minutoSaida: '00' | '15' | '30' | '45';
  maxPessoas: string;
  quorumMinimo: string;
  valorPorPessoa: string;
  valorGuia: string;
  taxaAplicada: string;
  pontoPartida: string;
  gps: string;
  experiences: string[];
  hasTransport: boolean;
  transportRows: TransportRow[];
  guiaId: string;
  guiaEmail: string;
  showGuia: boolean;
};

type VehicleType = 'passeio5' | 'suv5' | 'suv7' | 'taxi5' | 'van16' | 'van22';

const VEHICLE_OPTIONS: { value: VehicleType; label: string; cityKey: keyof NonNullable<import('../lib/experienciasStorage').TransportOpcoes> }[] = [
  { value: 'passeio5', label: 'Passeio (5 lugares)', cityKey: 'passeio' },
  { value: 'suv5', label: 'SUV (5 lugares)', cityKey: 'suv4x4' },
  { value: 'suv7', label: 'SUV (7 lugares)', cityKey: 'suv7lugares' },
  { value: 'taxi5', label: 'Taxi (5 lugares)', cityKey: 'taxi' },
  { value: 'van16', label: 'van 16 (16 lugares)', cityKey: 'van16' },
  { value: 'van22', label: 'van 22 (22 lugares)', cityKey: 'van22' },
];

function getVehicleOptionsForCity(cityName: string): { value: VehicleType; label: string; blocked: boolean }[] {
  const opts = getCidadeByNome(cityName)?.transportOpcoes;
  return VEHICLE_OPTIONS.map((o) => ({
    value: o.value,
    label: o.label,
    blocked: !opts?.[o.cityKey],
  }));
}

function getMaxSeatsForVehicle(type: VehicleType): number {
  const map: Record<VehicleType, number> = {
    passeio5: 4,
    suv5: 4,
    suv7: 6,
    taxi5: 4,
    van16: 15,
    van22: 21,
  };
  return map[type] ?? 4;
}

function normalizeVehicleType(v: string): VehicleType {
  if (['passeio5', 'suv5', 'suv7', 'taxi5', 'van16', 'van22'].includes(v)) return v as VehicleType;
  if (v === '5') return 'passeio5';
  if (v === '7') return 'suv7';
  return 'passeio5';
}

type TransportRow = {
  id: string;
  vehicleSize: VehicleType;
  seatsAvailable: string;
  valorIndividual: string;
  taxaAplicada: string;
  valorGuia: string;
  quorum: string;
};

type ParsedCoordinates = { lat: number; lng: number };

function isValidCoordinates(lat: number, lng: number): boolean {
  return Number.isFinite(lat) && Number.isFinite(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
}

function parseGoogleMapsInput(raw: string): ParsedCoordinates | null {
  const value = raw.trim();
  if (!value) return null;
  const patterns = [
    /@(-?\d{1,2}(?:\.\d+)?),\s*(-?\d{1,3}(?:\.\d+)?)/i, // .../@lat,lng,...
    /!3d(-?\d{1,2}(?:\.\d+)?)!4d(-?\d{1,3}(?:\.\d+)?)/i, // ...!3dlat!4dlng...
    /[?&]q=(-?\d{1,2}(?:\.\d+)?),\s*(-?\d{1,3}(?:\.\d+)?)/i, // ...?q=lat,lng
    /(-?\d{1,2}(?:\.\d+)?)\s*,\s*(-?\d{1,3}(?:\.\d+)?)/, // lat,lng
  ];
  for (const pattern of patterns) {
    const match = value.match(pattern);
    if (!match) continue;
    const lat = Number(match[1]);
    const lng = Number(match[2]);
    if (isValidCoordinates(lat, lng)) return { lat, lng };
  }
  return null;
}

declare global {
  interface Window {
    google?: {
      maps: {
        Map: new (el: HTMLElement, opts: Record<string, unknown>) => {
          setCenter: (coords: ParsedCoordinates) => void;
          setZoom: (zoom: number) => void;
        };
        Marker: new (opts: Record<string, unknown>) => {
          setPosition: (coords: ParsedCoordinates) => void;
          addListener: (event: string, cb: () => void) => void;
          getPosition: () => { lat: () => number; lng: () => number } | null;
        };
        Geocoder: new () => {
          geocode: (
            req: Record<string, unknown>,
            cb: (results: Array<{ formatted_address?: string }> | null, status: string) => void
          ) => void;
        };
        places: {
          Autocomplete: new (
            input: HTMLInputElement,
            opts?: Record<string, unknown>
          ) => {
            addListener: (event: string, cb: () => void) => void;
            getPlace: () => {
              formatted_address?: string;
              geometry?: { location?: { lat: () => number; lng: () => number } };
            };
          };
        };
      };
    };
  }
}

function toDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function getDaysInRange(startDate: string, endDate: string): string[] {
  if (!startDate || !endDate) return [];
  const a = new Date(`${startDate}T00:00:00`);
  const b = new Date(`${endDate}T00:00:00`);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return [];
  const start = a <= b ? a : b;
  const end = a <= b ? b : a;

  const out: string[] = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    out.push(toDateStr(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return out;
}

function calcularRecebimentoGuia(valorPorPessoa: number, taxaAplicadaPct: number): number {
  if (!Number.isFinite(valorPorPessoa) || valorPorPessoa <= 0) return 0;
  const taxa = Number.isFinite(taxaAplicadaPct) ? Math.max(0, Math.min(100, taxaAplicadaPct)) : 0;
  return Math.round(valorPorPessoa * (1 - taxa / 100));
}

function getQuorumMinimoCalculado(
  diariaMinimaTotal: number,
  valorPorPessoa: number,
  vagasMax: number
): number {
  if (!Number.isFinite(valorPorPessoa) || valorPorPessoa <= 0 || !Number.isFinite(diariaMinimaTotal) || diariaMinimaTotal <= 0) {
    return Math.max(1, Math.min(4, vagasMax));
  }
  const minimo = Math.ceil(diariaMinimaTotal / valorPorPessoa);
  return Math.max(1, Math.min(minimo, vagasMax));
}

/** Valor mínimo por pessoa na excursão: diária mínima ÷ máximo de pessoas do grupo. */
function getValorMinimoPorPessoaExcursao(diariaMinima: number, maxPessoas: number): number | null {
  if (!Number.isFinite(diariaMinima) || diariaMinima <= 0 || !Number.isFinite(maxPessoas) || maxPessoas <= 0) return null;
  return Math.ceil(diariaMinima / maxPessoas);
}

export default function NovaExcursaoPage() {
  const { id: editId } = useParams();
  const isEditMode = Boolean(editId);
  const navigate = useNavigate();
  const { showAlert } = useAdminDialog();
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectingStartDate, setSelectingStartDate] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(() => new Date());
  const [configs, setConfigs] = useState<Record<string, DayConfig>>({});
  const [saving, setSaving] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [globalPeriodConfig, setGlobalPeriodConfig] = useState<{
    city: string;
    horaSaida: string;
    minutoSaida: DayConfig['minutoSaida'];
    pontoPartida: string;
    gps: string;
  }>({
    city: '',
    horaSaida: '09',
    minutoSaida: '00',
    pontoPartida: '',
    gps: '',
  });
  const [editExcursao, setEditExcursao] = useState<Excursao | null>(null);
  const [selectedDayTab, setSelectedDayTab] = useState<string | null>(null);
  const [activeMainTab, setActiveMainTab] = useState<'embarque' | string>('embarque');
  const [locationMode, setLocationMode] = useState<'address' | 'gps'>('address');
  const [mapsReady, setMapsReady] = useState(false);
  const [mapsError, setMapsError] = useState<string | null>(null);
  const [locationCoords, setLocationCoords] = useState<ParsedCoordinates | null>(null);
  const editLoadedRef = useRef(false);
  const addressInputRef = useRef<HTMLInputElement | null>(null);
  const mapRef = useRef<{ setCenter: (coords: ParsedCoordinates) => void; setZoom: (zoom: number) => void } | null>(null);
  const markerRef = useRef<{
    setPosition: (coords: ParsedCoordinates) => void;
    addListener: (event: string, cb: () => void) => void;
    getPosition: () => { lat: () => number; lng: () => number } | null;
  } | null>(null);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const autocompleteRef = useRef<{ addListener: (event: string, cb: () => void) => void; getPlace: () => { formatted_address?: string; geometry?: { location?: { lat: () => number; lng: () => number } } } } | null>(null);

  const { catalogVersion } = useAdminCatalog();
  const cidades = useMemo(() => getCidades(), [catalogVersion]);
  const experiencias = useMemo(
    () => getExperiencias().filter((e) => (e.status ?? 'rascunho') === 'publicado'),
    [catalogVersion]
  );
  const tarifarios = useMemo(() => getTarifarios(), [catalogVersion]);
  const guias = useMemo(() => getGuias().filter(guiaApto), [catalogVersion]);
  const periodoDias = useMemo(() => getDaysInRange(startDate, endDate), [startDate, endDate]);
  const experienciasById = useMemo(
    () => Object.fromEntries(experiencias.map((e) => [e.id, e])) as Record<string, ExperienciaAdmin>,
    [experiencias]
  );
  const tarifariosById = useMemo(
    () => Object.fromEntries(tarifarios.map((t) => [t.id, t])),
    [tarifarios]
  );
  const minutosValidos: Array<'00' | '15' | '30' | '45'> = ['00', '15', '30', '45'];
  const horas = Array.from({ length: 24 }, (_, h) => String(h).padStart(2, '0'));
  const taxaDescontoVeiculoPadrao = useMemo(() => {
    const cfg = getTarifarioGlobalConfig();
    const pct = cfg?.descontoVeiculo ?? 10;
    return String(Number.isFinite(pct) ? pct : 10);
  }, [catalogVersion]);

  const calcularRecebimentoGuiaTransporte = (valorIndividual: number, taxaPct: number): number => {
    if (!Number.isFinite(valorIndividual) || valorIndividual <= 0) return 0;
    const taxa = Number.isFinite(taxaPct) ? Math.max(0, Math.min(100, taxaPct)) : 0;
    return Math.round(valorIndividual * (1 - taxa / 100));
  };

  const googleMapsApiKey = (import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined)?.trim();

  const createTransportRow = (
    valorIndividual = '',
    vehicleSize: VehicleType = 'passeio5',
    seatsAvailable = '1',
    taxaAplicada?: string
  ): TransportRow => {
    const valor = Number(valorIndividual) || 0;
    const taxa = Number(taxaAplicada ?? taxaDescontoVeiculoPadrao) || 0;
    const valorGuia = calcularRecebimentoGuiaTransporte(valor, taxa);
    return {
      id: `tr-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      vehicleSize,
      seatsAvailable,
      valorIndividual,
      taxaAplicada: taxaAplicada ?? taxaDescontoVeiculoPadrao,
      valorGuia: String(valorGuia),
      quorum: seatsAvailable,
    };
  };

  const getDefaultDayConfig = (): DayConfig => {
    const cityFallback = globalPeriodConfig.city || cidades[0]?.cidade || 'Alto Paraíso de Goiás';
    return {
      city: cityFallback,
      horaSaida: globalPeriodConfig.horaSaida || '09',
      minutoSaida: globalPeriodConfig.minutoSaida || '00',
      maxPessoas: '',
      quorumMinimo: '',
      valorPorPessoa: '',
      valorGuia: '',
      taxaAplicada: '',
      pontoPartida: globalPeriodConfig.pontoPartida || '',
      gps: globalPeriodConfig.gps || '',
      experiences: [],
      hasTransport: false,
      transportRows: [],
      guiaId: '',
      guiaEmail: '',
      showGuia: false,
    };
  };

  const ensureConfig = (dateStr: string): DayConfig => {
    return configs[dateStr] || getDefaultDayConfig();
  };

  const normalizarCidade = (value: string): string =>
    value.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

  const getCidadeIdByNome = (cityName: string): string | undefined => {
    const cityNorm = normalizarCidade(cityName);
    return cidades.find((c) => normalizarCidade(c.cidade) === cityNorm)?.id;
  };

  const experienciaDisponivelNaCidade = (exp: ExperienciaAdmin, cityName: string): boolean => {
    if (!cityName.trim()) return true;
    const cidadeNorm = normalizarCidade(cityName);
    const cidadeId = getCidadeIdByNome(cityName);
    const embarques = exp.cidadesEmbarque ?? [];
    if (embarques.length === 0) return false;
    return embarques.some((ce) => {
      const nomeMatch = ce.cidadeNome ? normalizarCidade(ce.cidadeNome) === cidadeNorm : false;
      const idMatch = cidadeId ? ce.cidadeId === cidadeId : false;
      return nomeMatch || idMatch;
    });
  };

  const getExperienciasDisponiveisNaCidade = (cityName: string): ExperienciaAdmin[] =>
    experiencias.filter((exp) => experienciaDisponivelNaCidade(exp, cityName));

  const filtrarExperienciasPorCidade = (experienceIds: string[], cityName: string): string[] =>
    experienceIds.filter((id) => {
      const exp = experienciasById[id];
      return exp ? experienciaDisponivelNaCidade(exp, cityName) : false;
    });

  useEffect(() => {
    if (!isEditMode || !editId || editLoadedRef.current) return;
    const map = getSharedExcursoesFromStorage();
    const existing = map[editId];
    if (!existing) {
      window.alert('Não foi possível carregar a excursão para edição.');
      navigate('/excursoes', { replace: true });
      return;
    }
    setEditExcursao(existing);

    const [horaSaidaRaw = '09', minutoRaw = '00'] = (existing.horarioSaida || '09:00').split(':');
    const minutoSaida = (['00', '15', '30', '45'].includes(minutoRaw) ? minutoRaw : '00') as DayConfig['minutoSaida'];
    const experienciaIds = (existing.experiences || [])
      .map((idx) => experiencias[idx - 1]?.id)
      .filter((v): v is string => Boolean(v));

    const vehicleSize: VehicleType =
      (existing.vagasComTransporte ?? 0) > 4 ? 'suv7' : 'passeio5';
    const maxSeats = getMaxSeatsForVehicle(vehicleSize);
    const seats = existing.vagasComTransporte != null ? Math.max(1, Math.min(existing.vagasComTransporte, maxSeats)) : 1;
    const quorum = existing.quorumMinimoTransporte != null ? Math.max(1, Math.min(existing.quorumMinimoTransporte, maxSeats)) : maxSeats;

    const taxaFromTarifario = (() => {
      if (experienciaIds.length === 0) return '';
      const exp = experiencias.find((e) => e.id === experienciaIds[0]);
      if (!exp) return '';
      const cidadeNorm = (existing.city || '').trim().toLowerCase();
      const fromCidade = exp.cidadesEmbarque?.find((ce) => (ce.cidadeNome || '').trim().toLowerCase() === cidadeNorm)?.tarifarioId
        ?? exp.cidadesEmbarque?.find((ce) => !!ce.tarifarioId)?.tarifarioId;
      if (!fromCidade) return '';
      const t = tarifarios.find((x) => x.id === fromCidade) as { taxaGeralAgencia?: number } | undefined;
      const taxa = t?.taxaGeralAgencia;
      return Number.isFinite(taxa) ? String(taxa) : '';
    })();
    const cfg: DayConfig = {
      city: existing.city || (cidades[0]?.cidade || 'Alto Paraíso de Goiás'),
      horaSaida: horaSaidaRaw.padStart(2, '0'),
      minutoSaida,
      maxPessoas: String(existing.maxPessoas || ''),
      quorumMinimo: String(existing.quorumMinimo || ''),
      valorPorPessoa: String(existing.valorPorPessoa || ''),
      valorGuia: String(existing.valorGuia || ''),
      taxaAplicada: taxaFromTarifario || '',
      pontoPartida: existing.pontoPartida || '',
      gps: existing.gps || '',
      experiences: experienciaIds,
      hasTransport: Boolean(existing.hasTransport),
      transportRows: existing.hasTransport
        ? [
            {
              id: `tr-edit-${existing.id}`,
              vehicleSize,
              seatsAvailable: String(seats),
              valorIndividual: String(existing.valorVagaTransporte || ''),
              taxaAplicada: taxaDescontoVeiculoPadrao,
              valorGuia: String(calcularRecebimentoGuiaTransporte(Number(existing.valorVagaTransporte || 0), Number(taxaDescontoVeiculoPadrao) || 0)),
              quorum: String(quorum),
            },
          ]
        : [],
      guiaId: existing.guiaId || '',
      guiaEmail: existing.guiaEmail || '',
      showGuia: Boolean(existing.guiaId || existing.guiaEmail),
    };
    const valorP = Number(cfg.valorPorPessoa) || 0;
    const taxaP = Number(cfg.taxaAplicada) || 0;
    cfg.valorGuia = String(calcularRecebimentoGuia(valorP, taxaP));

    setStartDate(existing.date);
    setEndDate(existing.date);
    setConfigs({ [existing.date]: cfg });
    setGlobalPeriodConfig({
      city: cfg.city,
      horaSaida: cfg.horaSaida,
      minutoSaida: cfg.minutoSaida,
      pontoPartida: cfg.pontoPartida,
      gps: cfg.gps,
    });
    editLoadedRef.current = true;
  }, [isEditMode, editId, navigate, experiencias, cidades, tarifarios]);

  useEffect(() => {
    if (!globalPeriodConfig.city && cidades.length > 0) {
      setGlobalPeriodConfig((prev) => ({ ...prev, city: cidades[0].cidade }));
    }
  }, [cidades, globalPeriodConfig.city]);

  useEffect(() => {
    setConfigs((prev) => {
      let changed = false;
      const next = { ...prev };
      Object.keys(next).forEach((dayKey) => {
        const cfg = next[dayKey];
        if (!cfg) return;
        const vagasNum = Number(cfg.maxPessoas) || 0;
        const quorumNum = Number(cfg.quorumMinimo) || 0;
        if (!cfg.maxPessoas && cfg.quorumMinimo) {
          next[dayKey] = { ...cfg, quorumMinimo: '' };
          changed = true;
        } else if (vagasNum > 0 && quorumNum > vagasNum) {
          next[dayKey] = { ...cfg, quorumMinimo: cfg.maxPessoas };
          changed = true;
        }
        cfg.transportRows?.forEach((row, i) => {
          const seats = Number(row.seatsAvailable) || 0;
          const q = Number(row.quorum) || 0;
          if (seats > 0 && q > seats) {
            const rows = [...(next[dayKey]?.transportRows ?? cfg.transportRows)];
            rows[i] = { ...row, quorum: row.seatsAvailable };
            next[dayKey] = { ...(next[dayKey] ?? cfg), transportRows: rows };
            changed = true;
          }
        });
      });
      return changed ? next : prev;
    });
  }, [configs, periodoDias]);

  useEffect(() => {
    if (periodoDias.length === 0) {
      setSelectedDayTab(null);
      setActiveMainTab('embarque');
      return;
    }
    if (!selectedDayTab || !periodoDias.includes(selectedDayTab)) {
      setSelectedDayTab(periodoDias[0]);
    }
    if (activeMainTab !== 'embarque' && !periodoDias.includes(activeMainTab)) {
      setActiveMainTab(periodoDias[0]);
    }
  }, [periodoDias, selectedDayTab, activeMainTab]);

  const updateConfig = (dateStr: string, patch: Partial<DayConfig>) => {
    setConfigs((prev) => ({ ...prev, [dateStr]: { ...ensureConfig(dateStr), ...patch } }));
  };

  const getLimiteMaximoPorAtividades = (experienceIds: string[]): number | null => {
    const limites = experienceIds
      .map((id) => Number(experienciasById[id]?.maxParticipantesPorGuia ?? 0))
      .filter((n) => Number.isFinite(n) && n > 0);
    if (limites.length === 0) return null;
    return Math.min(...limites);
  };

  const getTarifarioIdDaExperienciaNoDia = (exp: ExperienciaAdmin, cityName: string): string | undefined => {
    const cidadeNorm = cityName.trim().toLowerCase();
    const fromCidade =
      exp.cidadesEmbarque?.find((ce) => (ce.cidadeNome || '').trim().toLowerCase() === cidadeNorm)?.tarifarioId;
    if (fromCidade) return fromCidade;
    return exp.cidadesEmbarque?.find((ce) => !!ce.tarifarioId)?.tarifarioId;
  };

  const calcularValoresTarifario5Pax = (experienceIds: string[], cityName: string): { valorPorPessoa: number; valorGuia: number } | null => {
    let somaValorPorPessoa = 0;
    let somaValorGuiaPorPessoa = 0;
    let encontrouTarifario = false;

    experienceIds.forEach((id) => {
      const exp = experienciasById[id];
      if (!exp) return;
      const tarifarioId = getTarifarioIdDaExperienciaNoDia(exp, cityName);
      if (!tarifarioId) return;
      const tarifario = tarifariosById[tarifarioId];
      if (!tarifario) return;

      // Regra: usar Individual (PAX 4) Excursão Unitário da tarifa da(s) atividade(s).
      const row4 = tarifario.passeioExclusivo?.[3];
      const individualPax4 = Number(row4?.brl ?? 0);
      const diariaGuia4 = Number(row4?.diariaGuiaBrl ?? 0);
      if (individualPax4 <= 0) return;

      encontrouTarifario = true;
      somaValorPorPessoa += individualPax4;
      // Valor guia por inscrito = diária do guia (4 pax) / 4.
      const guiaPorPessoa = diariaGuia4 > 0 ? diariaGuia4 / 4 : individualPax4 * 0.7;
      somaValorGuiaPorPessoa += guiaPorPessoa;
    });

    if (!encontrouTarifario) return null;
    return {
      valorPorPessoa: Math.round(somaValorPorPessoa),
      valorGuia: Math.round(somaValorGuiaPorPessoa),
    };
  };

  const calcularValorIndividualTranslado = (experienceIds: string[], cityName: string): number | null => {
    let total = 0;
    let encontrouTarifario = false;

    experienceIds.forEach((id) => {
      const exp = experienciasById[id];
      if (!exp) return;
      const tarifarioId = getTarifarioIdDaExperienciaNoDia(exp, cityName);
      if (!tarifarioId) return;
      const tarifario = tarifariosById[tarifarioId];
      if (!tarifario) return;
      const valor = Number(tarifario.excursaoIndividualTranslado?.brl ?? 0);
      if (valor <= 0) return;
      encontrouTarifario = true;
      total += valor;
    });

    if (!encontrouTarifario) return null;
    return Math.round(total);
  };

  const getTaxaAgenciaFromTarifario = (experienceIds: string[], cityName: string): number | null => {
    if (experienceIds.length === 0) return null;
    const exp = experienciasById[experienceIds[0]];
    if (!exp) return null;
    const tarifarioId = getTarifarioIdDaExperienciaNoDia(exp, cityName);
    if (!tarifarioId) return null;
    const tarifario = tarifariosById[tarifarioId] as { taxaGeralAgencia?: number } | undefined;
    const taxa = tarifario?.taxaGeralAgencia;
    return Number.isFinite(taxa) ? Number(taxa) : null;
  };

  const getDiariaMinimaTarifario = (experienceIds: string[], cityName: string): number | null => {
    let total = 0;
    let encontrou = false;
    experienceIds.forEach((id) => {
      const exp = experienciasById[id];
      if (!exp) return;
      const tarifarioId = getTarifarioIdDaExperienciaNoDia(exp, cityName);
      if (!tarifarioId) return;
      const tarifario = tarifariosById[tarifarioId];
      if (!tarifario?.passeioExclusivo?.[3]) return;
      const brl4 = Number(tarifario.passeioExclusivo[3].brl ?? 0);
      if (brl4 <= 0) return;
      encontrou = true;
      total += brl4 * 4;
    });
    return encontrou ? Math.round(total) : null;
  };

  const getSugeridoExcursaoIndividualBrl = (experienceIds: string[], cityName: string): number | null => {
    let total = 0;
    let encontrouTarifario = false;
    experienceIds.forEach((id) => {
      const exp = experienciasById[id];
      if (!exp) return;
      const tarifarioId = getTarifarioIdDaExperienciaNoDia(exp, cityName);
      if (!tarifarioId) return;
      const tarifario = tarifariosById[tarifarioId];
      if (!tarifario) return;
      const valor = Number(tarifario.excursaoIndividual?.brl ?? 0);
      if (valor <= 0) return;
      encontrouTarifario = true;
      total += valor;
    });
    return encontrouTarifario ? Math.round(total) : null;
  };

  const aplicarRegrasAutomaticasDoDia = (dateStr: string, nextExperiences: string[], cityName: string) => {
    const currentCfg = ensureConfig(dateStr);
    if (nextExperiences.length === 0) {
      updateConfig(dateStr, {
        experiences: nextExperiences,
        maxPessoas: '',
        quorumMinimo: '',
        valorPorPessoa: '',
        valorGuia: '',
        taxaAplicada: '',
      });
      return;
    }

    const limiteMaximo = getLimiteMaximoPorAtividades(nextExperiences);
    const valores = calcularValoresTarifario5Pax(nextExperiences, cityName);
    const valorTransladoAuto = calcularValorIndividualTranslado(nextExperiences, cityName);
    const nextPatch: Partial<DayConfig> = {
      experiences: nextExperiences,
    };

    if (limiteMaximo && limiteMaximo > 0) {
      nextPatch.maxPessoas = String(limiteMaximo);
      // Marcar automaticamente o menor quórum possível.
      const valorPessoa = valores?.valorPorPessoa ?? 0;
      const diariaMinima = getDiariaMinimaTarifario(nextExperiences, cityName);
      const quorumMin = (diariaMinima != null && valorPessoa > 0)
        ? getQuorumMinimoCalculado(diariaMinima, valorPessoa, limiteMaximo)
        : 1;
      nextPatch.quorumMinimo = String(quorumMin);
    }

    const taxaFromTarifario = getTaxaAgenciaFromTarifario(nextExperiences, cityName);
    if (taxaFromTarifario != null) {
      nextPatch.taxaAplicada = String(taxaFromTarifario);
    }
    if (valores) {
      nextPatch.valorPorPessoa = String(valores.valorPorPessoa);
      const taxaPct = taxaFromTarifario ?? (Number(currentCfg.taxaAplicada) || 0);
      nextPatch.valorGuia = String(calcularRecebimentoGuia(valores.valorPorPessoa, taxaPct));
    }

    if (currentCfg.hasTransport && valorTransladoAuto != null) {
      nextPatch.transportRows = currentCfg.transportRows.map((row) => {
        const taxa = Number(row.taxaAplicada ?? taxaDescontoVeiculoPadrao) || 0;
        return {
          ...row,
          valorIndividual: String(valorTransladoAuto),
          valorGuia: String(calcularRecebimentoGuiaTransporte(valorTransladoAuto, taxa)),
        };
      });
    }

    updateConfig(dateStr, nextPatch);
  };

  const applyGlobalFieldsToAllDays = (patch: Partial<Pick<DayConfig, 'city' | 'horaSaida' | 'minutoSaida' | 'pontoPartida' | 'gps'>>) => {
    setGlobalPeriodConfig((prev) => ({ ...prev, ...patch }));
    if (periodoDias.length === 0) return;
    setConfigs((prev) => {
      const next: Record<string, DayConfig> = { ...prev };
      periodoDias.forEach((dayKey) => {
        const current = prev[dayKey] || getDefaultDayConfig();
        const experienciasFiltradas = patch.city
          ? filtrarExperienciasPorCidade(current.experiences, patch.city)
          : current.experiences;
        const updated: DayConfig = { ...current, ...patch, experiences: experienciasFiltradas };
        if (patch.city) {
          if (experienciasFiltradas.length === 0) {
            updated.maxPessoas = '';
            updated.quorumMinimo = '';
            updated.valorPorPessoa = '';
            updated.valorGuia = '';
            updated.taxaAplicada = '';
          }
          const limiteMaximo = getLimiteMaximoPorAtividades(experienciasFiltradas);
          const valores = calcularValoresTarifario5Pax(experienciasFiltradas, patch.city);
          const valorTransladoAuto = calcularValorIndividualTranslado(experienciasFiltradas, patch.city);
          const taxaFromTarifario = getTaxaAgenciaFromTarifario(experienciasFiltradas, patch.city);
          if (limiteMaximo != null && limiteMaximo > 0) {
            updated.maxPessoas = String(limiteMaximo);
            // Marcar automaticamente o menor quórum possível.
            const valorPessoa = valores?.valorPorPessoa ?? 0;
            const diariaMinima = getDiariaMinimaTarifario(experienciasFiltradas, patch.city);
            const quorumMin = (diariaMinima != null && valorPessoa > 0)
              ? getQuorumMinimoCalculado(diariaMinima, valorPessoa, limiteMaximo)
              : 1;
            updated.quorumMinimo = String(quorumMin);
          }
          if (taxaFromTarifario != null) {
            updated.taxaAplicada = String(taxaFromTarifario);
          }
          if (valores) {
            updated.valorPorPessoa = String(valores.valorPorPessoa);
            const taxaPct = taxaFromTarifario ?? (Number(updated.taxaAplicada) || 0);
            updated.valorGuia = String(calcularRecebimentoGuia(valores.valorPorPessoa, taxaPct));
          }
          if (updated.hasTransport && valorTransladoAuto != null) {
            updated.transportRows = updated.transportRows.map((row) => {
              const taxa = Number(row.taxaAplicada ?? taxaDescontoVeiculoPadrao) || 0;
              return {
                ...row,
                valorIndividual: String(valorTransladoAuto),
                valorGuia: String(calcularRecebimentoGuiaTransporte(valorTransladoAuto, taxa)),
              };
            });
          }
        }
        next[dayKey] = updated;
      });
      return next;
    });
  };

  const applyCoordinatesToForm = (coords: ParsedCoordinates, maybeAddress?: string) => {
    const normalized = `${coords.lat.toFixed(6)},${coords.lng.toFixed(6)}`;
    setLocationCoords(coords);
    applyGlobalFieldsToAllDays({
      gps: normalized,
      ...(maybeAddress ? { pontoPartida: maybeAddress } : {}),
    });
  };

  const handleApplyGpsInput = () => {
    const parsed = parseGoogleMapsInput(globalPeriodConfig.gps);
    if (!parsed) {
      window.alert('Não foi possível ler o GPS. Informe "lat,lng" ou cole um link válido do Google Maps.');
      return;
    }
    if (mapsReady && window.google?.maps) {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: parsed }, (results, status) => {
        const address = status === 'OK' ? results?.[0]?.formatted_address : undefined;
        applyCoordinatesToForm(parsed, address);
      });
      return;
    }
    applyCoordinatesToForm(parsed);
  };

  useEffect(() => {
    if (!googleMapsApiKey) return;
    if (window.google?.maps?.places) {
      setMapsReady(true);
      setMapsError(null);
      return;
    }
    const scriptId = 'google-maps-api-script-naviex';
    const existing = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', () => setMapsReady(true), { once: true });
      existing.addEventListener('error', () => setMapsError('Não foi possível carregar o Google Maps.'), { once: true });
      return;
    }
    const script = document.createElement('script');
    script.id = scriptId;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(googleMapsApiKey)}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      setMapsReady(true);
      setMapsError(null);
    };
    script.onerror = () => setMapsError('Não foi possível carregar o Google Maps.');
    document.head.appendChild(script);
  }, [googleMapsApiKey]);

  useEffect(() => {
    const parsed = parseGoogleMapsInput(globalPeriodConfig.gps);
    if (parsed) setLocationCoords(parsed);
  }, [globalPeriodConfig.gps]);

  const geocodeAddressToMap = (address: string) => {
    if (!address?.trim() || !window.google?.maps) return;
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: address.trim(), region: 'br' }, (results, status) => {
      if (status === 'OK' && results?.[0]?.geometry?.location) {
        const lat = results[0].geometry.location.lat();
        const lng = results[0].geometry.location.lng();
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          applyCoordinatesToForm(
            { lat, lng },
            results[0].formatted_address || address
          );
        }
      }
    });
  };

  useEffect(() => {
    if (!mapsReady || !window.google?.maps || !addressInputRef.current || autocompleteRef.current) return;
    const autocomplete = new window.google.maps.places.Autocomplete(addressInputRef.current, {
      fields: ['formatted_address', 'geometry'],
      types: ['geocode'],
      componentRestrictions: { country: 'br' },
    });
    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      const lat = place.geometry?.location?.lat?.();
      const lng = place.geometry?.location?.lng?.();
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        geocodeAddressToMap(addressInputRef.current?.value || '');
        return;
      }
      applyCoordinatesToForm(
        { lat: Number(lat), lng: Number(lng) },
        place.formatted_address || addressInputRef.current?.value || ''
      );
    });
    autocompleteRef.current = autocomplete;
  }, [mapsReady]);

  useEffect(() => {
    if (!mapsReady || !window.google?.maps || !mapContainerRef.current || !locationCoords) return;
    if (!mapRef.current) {
      const map = new window.google.maps.Map(mapContainerRef.current, {
        center: locationCoords,
        zoom: 15,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
      });
      const marker = new window.google.maps.Marker({
        map,
        position: locationCoords,
        draggable: true,
      });
      marker.addListener('dragend', () => {
        const pos = marker.getPosition();
        if (!pos) return;
        const coords = { lat: pos.lat(), lng: pos.lng() };
        if (!isValidCoordinates(coords.lat, coords.lng)) return;
        const geocoder = new window.google!.maps.Geocoder();
        geocoder.geocode({ location: coords }, (results, status) => {
          const address = status === 'OK' ? results?.[0]?.formatted_address : undefined;
          applyCoordinatesToForm(coords, address);
        });
      });
      mapRef.current = map;
      markerRef.current = marker;
      return;
    }
    mapRef.current.setCenter(locationCoords);
    mapRef.current.setZoom(15);
    markerRef.current?.setPosition(locationCoords);
  }, [mapsReady, locationCoords]);

  const getAllowedExperienceIds = (selected: string[], availableIds: Set<string>): Set<string> => {
    if (selected.length === 0) {
      return new Set(
        experiencias
          .filter((e) => !e.naoEhAtividadePrincipal && availableIds.has(e.id))
          .map((e) => e.id)
      );
    }
    const principal = experienciasById[selected[0]];
    if (!principal) return new Set([selected[0]]);
    const allowed = new Set<string>([principal.id]);
    (principal.relacionados ?? []).forEach((row) => {
      (row.experienciaIds ?? []).forEach((id) => allowed.add(id));
    });
    return new Set(Array.from(allowed).filter((id) => availableIds.has(id)));
  };

  const toggleExperience = (day: string, expId: string) => {
    const cfg = ensureConfig(day);
    const already = cfg.experiences.includes(expId);
    if (already) {
      const next = cfg.experiences.filter((id) => id !== expId);
      aplicarRegrasAutomaticasDoDia(day, next, cfg.city);
      return;
    }
    const disponiveisIds = new Set(getExperienciasDisponiveisNaCidade(cfg.city).map((exp) => exp.id));
    const allowed = getAllowedExperienceIds(cfg.experiences, disponiveisIds);
    if (!allowed.has(expId)) {
      window.alert(
        'Não foi possível selecionar esta atividade.\n\n' +
        'Selecione apenas atividades relacionadas à atividade principal.'
      );
      return;
    }
    aplicarRegrasAutomaticasDoDia(day, [...cfg.experiences, expId], cfg.city);
  };

  const addTransportRow = (day: string) => {
    const cfg = ensureConfig(day);
    if (cfg.transportRows.length >= 3) {
      window.alert('Máximo de 3 veículos permitidos.');
      return;
    }
    const totalAssentos = cfg.transportRows.reduce((acc, r) => acc + (Number(r.seatsAvailable) || 0), 0);
    if (totalAssentos >= 12) {
      window.alert('Total máximo de 12 assentos com translado já atingido.');
      return;
    }
    const valorTransladoAuto = calcularValorIndividualTranslado(cfg.experiences, cfg.city);
    updateConfig(day, { transportRows: [...cfg.transportRows, createTransportRow(valorTransladoAuto != null ? String(valorTransladoAuto) : '')] });
  };

  const removeTransportRow = (day: string, rowId: string) => {
    const cfg = ensureConfig(day);
    const index = cfg.transportRows.findIndex((r) => r.id === rowId);
    // Regra: só remove se houver no mínimo 2 linhas e nunca a primeira linha.
    if (cfg.transportRows.length < 2 || index <= 0) return;
    updateConfig(day, { transportRows: cfg.transportRows.filter((r) => r.id !== rowId) });
  };

  const updateTransportRow = (day: string, rowId: string, patch: Partial<TransportRow>) => {
    const cfg = ensureConfig(day);
    updateConfig(day, {
      transportRows: cfg.transportRows.map((r) => {
        if (r.id !== rowId) return r;
        const next = { ...r, ...patch };
        const vehicleType = normalizeVehicleType(next.vehicleSize);
        next.vehicleSize = vehicleType;
        if (patch.vehicleSize && !VEHICLE_OPTIONS.find((o) => o.value === vehicleType)?.blocked) {
          const maxSeats = getMaxSeatsForVehicle(vehicleType);
          const seatsAtual = Math.min(maxSeats, Math.max(1, Number(next.seatsAvailable) || 1));
          next.seatsAvailable = String(seatsAtual);
          const quorumAtual = Number(next.quorum) || 1;
          if (quorumAtual > maxSeats) next.quorum = String(maxSeats);
          else if (quorumAtual > seatsAtual) next.quorum = String(seatsAtual);
        }
        if (patch.seatsAvailable !== undefined) {
          const maxSeats = getMaxSeatsForVehicle(vehicleType);
          const seatsRequested = Math.min(maxSeats, Math.max(1, Number(patch.seatsAvailable)));
          const outrosAssentos = cfg.transportRows
            .filter((x) => x.id !== rowId)
            .reduce((a, x) => a + (Number(x.seatsAvailable) || 0), 0);
          const seats = Math.max(1, Math.min(seatsRequested, 12 - outrosAssentos));
          next.seatsAvailable = String(seats);
          const quorumAtual = Number(next.quorum) || 1;
          if (quorumAtual > seats) next.quorum = String(seats);
        }
        if (patch.valorIndividual !== undefined || patch.taxaAplicada !== undefined) {
          const valor = Number(next.valorIndividual) || 0;
          const taxa = Number(next.taxaAplicada ?? taxaDescontoVeiculoPadrao) || 0;
          next.valorGuia = String(calcularRecebimentoGuiaTransporte(valor, taxa));
        }
        return next;
      }),
    });
  };

  const getSelectedCount = (experienceId: string): number => {
    return periodoDias.reduce((acc, day) => {
      const dayCfg = ensureConfig(day);
      return dayCfg.experiences.includes(experienceId) ? acc + 1 : acc;
    }, 0);
  };

  const formatDate = (dateStr: string): string => {
    if (!dateStr) return '';
    const [y, m, d] = dateStr.split('-');
    if (!y || !m || !d) return dateStr;
    return `${d}/${m}/${y}`;
  };

  const formatDateDDMMAA = (dateStr: string): string => {
    if (!dateStr) return '';
    const [y, m, d] = dateStr.split('-');
    if (!y || !m || !d) return dateStr;
    return `${d}/${m}/${y.slice(-2)}`;
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    return {
      year,
      month,
      daysInMonth: lastDay.getDate(),
      startingDayOfWeek: firstDay.getDay(),
    };
  };

  const handleDateSelect = (year: number, month: number, day: number) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    if (selectingStartDate) {
      setStartDate(dateStr);
      setEndDate('');
      setSelectingStartDate(false);
      return;
    }

    if (!startDate) {
      setStartDate(dateStr);
      setSelectingStartDate(false);
      return;
    }

    if (dateStr < startDate) {
      setStartDate(dateStr);
      setEndDate('');
      return;
    }

    setEndDate(dateStr);
    setSelectingStartDate(true);
    setShowDatePicker(false);
  };

  const renderCalendar = () => {
    const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentMonth);
    const monthNames = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const cells: React.ReactNode[] = [];

    for (let i = 0; i < startingDayOfWeek; i += 1) {
      cells.push(<div key={`empty-${i}`} className="h-10" />);
    }

    for (let day = 1; day <= daysInMonth; day += 1) {
      const date = new Date(year, month, day);
      date.setHours(0, 0, 0, 0);
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const isStartDate = dateStr === startDate;
      const isEndDate = dateStr === endDate;
      const isInRange = !!(startDate && endDate && dateStr >= startDate && dateStr <= endDate);
      const isDisabled = date.getTime() < today.getTime();

      cells.push(
        <button
          key={dateStr}
          type="button"
          disabled={isDisabled}
          onClick={() => !isDisabled && handleDateSelect(year, month, day)}
          className={`h-10 rounded-full text-sm transition-all ${
            isDisabled ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-orange-100'
          } ${
            isStartDate || isEndDate ? 'bg-orange-500 text-white font-bold' : ''
          } ${
            isInRange && !isStartDate && !isEndDate ? 'bg-orange-100 text-orange-700' : ''
          } ${
            !isInRange && !isStartDate && !isEndDate && !isDisabled ? 'text-gray-700' : ''
          }`}
        >
          {day}
        </button>
      );
    }

    return (
      <div className="mt-3 bg-white rounded-2xl border border-gray-200 shadow-sm p-4 max-w-md">
        <div className="flex items-center justify-between mb-4">
          <button
            type="button"
            onClick={() => setCurrentMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <h3 className="font-semibold text-gray-800">{monthNames[month]} {year}</h3>
          <button
            type="button"
            onClick={() => setCurrentMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>

        <div className="grid grid-cols-7 gap-2 mb-2">
          {['D', 'S', 'T', 'Q', 'Q', 'S', 'S'].map((d) => (
            <div key={d} className="h-8 flex items-center justify-center text-xs font-medium text-gray-500">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-2">{cells}</div>
      </div>
    );
  };

  const handleSave = async () => {
    if (!startDate || !endDate) {
      window.alert(
        'Não foi possível criar a excursão.\n\n' +
        'Defina o período da excursão.'
      );
      return;
    }
    if (periodoDias.length === 0) {
      window.alert(
        'Não foi possível criar a excursão.\n\n' +
        'O período informado é inválido.'
      );
      return;
    }

    const invalid = periodoDias.some((day) => {
      const cfg = ensureConfig(day);
      const vagasGrupo = Number(cfg.maxPessoas);
      const valorPorPessoa = Number(cfg.valorPorPessoa);
      const valorGuia = Number(cfg.valorGuia);
      const limiteMaximo = getLimiteMaximoPorAtividades(cfg.experiences);
      const quorum = Number(cfg.quorumMinimo);
      const diariaMinima = getDiariaMinimaTarifario(cfg.experiences, cfg.city);
      const valorMinimo = getValorMinimoPorPessoaExcursao(diariaMinima ?? 0, vagasGrupo);
      const valorAbaixoMinimo = valorMinimo != null && valorPorPessoa > 0 && valorPorPessoa < valorMinimo;
      return (
        !cfg.maxPessoas ||
        Number.isNaN(vagasGrupo) ||
        vagasGrupo < 1 ||
        (limiteMaximo != null && vagasGrupo > limiteMaximo) ||
        !cfg.valorPorPessoa ||
        Number.isNaN(valorPorPessoa) ||
        valorPorPessoa <= 0 ||
        valorAbaixoMinimo ||
        !cfg.valorGuia ||
        Number.isNaN(valorGuia) ||
        valorGuia <= 0 ||
        !cfg.quorumMinimo ||
        Number.isNaN(quorum) ||
        quorum < 1 ||
        quorum > vagasGrupo
      );
    });
    if (invalid) {
      window.alert(
        'Não foi possível criar a excursão.\n\n' +
        'Revise os campos obrigatórios em todos os dias:\n' +
        '- Vagas (grupo): de 1 até o limite da atividade selecionada\n' +
        '- Valor por pessoa: mínimo = diária mínima ÷ vagas do grupo\n' +
        '- Recebimento Guia / por PAX BRL: calculado (Valor por pessoa - Taxa aplicada)\n' +
        '- Quórum: calculado pela diária mínima do tarifário (mín. 1, máx. vagas do grupo)'
      );
      return;
    }
    const transladoInvalido = periodoDias.some((day) => {
      const cfg = ensureConfig(day);
      if (!cfg.hasTransport) return false;
      if (cfg.transportRows.length === 0) return true;
      if (cfg.transportRows.length > 3) return true;
      const totalSeats = cfg.transportRows.reduce((acc, r) => acc + (Number(r.seatsAvailable) || 0), 0);
      if (totalSeats > 12) return true;
      return cfg.transportRows.some((r) => {
        const maxSeats = getMaxSeatsForVehicle(normalizeVehicleType(r.vehicleSize));
        const seats = Number(r.seatsAvailable);
        const quorum = Number(r.quorum);
        const valorIndividual = Number(r.valorIndividual);
        return (
          !r.seatsAvailable ||
          seats <= 0 ||
          seats > maxSeats ||
          !r.quorum ||
          quorum > seats ||
          !r.valorIndividual ||
          Number.isNaN(valorIndividual) ||
          valorIndividual < 120 ||
          valorIndividual > 400
        );
      });
    });
    if (transladoInvalido) {
      window.alert(
        'Não foi possível criar a excursão.\n\n' +
        'Revise a opção com translado em todos os dias:\n' +
        '- Máximo 3 veículos e 12 assentos no total\n' +
        '- Veículo, assentos disponíveis, valor individual (R$ 120 a R$ 400)\n' +
        '- Taxa aplicada, Recebimento guia\n' +
        '- Quórum (deve ser ≤ assentos)'
      );
      return;
    }

    setSaving(true);
    try {
      if (isEditMode && editExcursao) {
        const day = periodoDias.includes(editExcursao.date) ? editExcursao.date : periodoDias[0];
        const cfg = ensureConfig(day);
        const inscritosAtuais = Number(editExcursao.inscritos || 0);
        const inscritosTransporteAtuais = Number(editExcursao.inscritosComTransporte || 0);
        const maxPessoas = Number(cfg.maxPessoas || 0);
        const quorum = Number(cfg.quorumMinimo || 0);
        const valorPorPessoa = Number(cfg.valorPorPessoa || 0);
        const valorGuia = Number(cfg.valorGuia || 0);
        if (inscritosAtuais > 0 && maxPessoas < inscritosAtuais) {
          window.alert('Vagas máximas não podem ser menores que os inscritos atuais.');
          return;
        }
        if (quorum < 1 || quorum > maxPessoas) {
          window.alert('Quórum deve ficar entre 1 e o número de vagas do grupo.');
          return;
        }
        if (valorPorPessoa <= 0 || valorGuia <= 0) {
          window.alert('Informe valores válidos para valor por pessoa e valor do guia.');
          return;
        }
        const diariaMinimaEdit = getDiariaMinimaTarifario(cfg.experiences, cfg.city);
        const valorMinimoEdit = getValorMinimoPorPessoaExcursao(diariaMinimaEdit ?? 0, maxPessoas);
        if (valorMinimoEdit != null && valorPorPessoa < valorMinimoEdit) {
          await showAlert(
            `Valor mínimo permitido para excursão é de R$ ${valorMinimoEdit.toLocaleString('pt-BR')}`,
            'Aviso',
            'Entendi'
          );
          return;
        }

        const rowsValidas = cfg.hasTransport
          ? cfg.transportRows.filter((r) => r.seatsAvailable && r.valorIndividual && r.quorum)
          : [];
        const vagasComTransporte = rowsValidas.reduce((acc, r) => acc + (Number(r.seatsAvailable) || 0), 0);
        if (!cfg.hasTransport && inscritosTransporteAtuais > 0) {
          window.alert('Não é possível desativar o translado quando já existem vagas com transporte compradas.');
          return;
        }
        if (cfg.hasTransport && inscritosTransporteAtuais > 0 && vagasComTransporte < inscritosTransporteAtuais) {
          window.alert('As vagas com transporte não podem ficar abaixo das reservas de transporte já compradas.');
          return;
        }

        const [hora = '09', minuto = '00'] = [cfg.horaSaida, cfg.minutoSaida];
        const mappedExperiences = cfg.experiences
          .map((id) => experiencias.findIndex((e) => e.id === id) + 1)
          .filter((n) => n > 0);
        const selectedExperienceTitles = cfg.experiences
          .map((id) => experiencias.find((e) => e.id === id)?.titulo?.trim())
          .filter((v): v is string => Boolean(v));
        const passeioTitle =
          selectedExperienceTitles.length > 0 ? selectedExperienceTitles.join(' + ') : `Excursão ${cfg.city}`;

        const updated: Excursao = {
          ...editExcursao,
          date: day,
          city: cfg.city,
          horarioSaida: `${hora}:${minuto}`,
          maxPessoas,
          quorumMinimo: quorum,
          valorPorPessoa,
          valorGuia,
          guiaId: cfg.guiaId || undefined,
          guiaEmail: cfg.guiaEmail || undefined,
          pontoPartida: cfg.pontoPartida || '',
          gps: cfg.gps || '',
          experiences: mappedExperiences,
          experienceAdminIds: cfg.experiences.length > 0 ? [...cfg.experiences] : undefined,
          hasTransport: cfg.hasTransport && rowsValidas.length > 0 && vagasComTransporte > 0,
          vagasComTransporte: cfg.hasTransport ? vagasComTransporte : undefined,
          valorVagaTransporte: cfg.hasTransport && rowsValidas.length > 0
            ? Math.trunc(Number(rowsValidas[0].valorIndividual) || 0)
            : undefined,
          quorumMinimoTransporte: cfg.hasTransport
            ? rowsValidas.reduce((acc, r) => acc + (Number(r.quorum) || 0), 0)
            : undefined,
          title: passeioTitle,
          subtitle: JSON.stringify({ valorGuia }),
        };

        const map = getSharedExcursoesFromStorage();
        map[updated.id] = updated;
        await fetch(apiUrl('/api/excursoes'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updated),
        }).catch(() => {});
        // Persistência local + sync
        saveSharedExcursoesToStorage(map);
        try {
          const allShared = Object.values(map);
          await fetch(apiUrl('/api/excursoes/sync?source=admin'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(allShared),
          });
        } catch {
          // best effort
        }
        window.dispatchEvent(new Event('excursao-updated'));
        setShowSuccessModal(true);
        return;
      }

      const toSync: Excursao[] = periodoDias.map((day) => {
        const cfg = ensureConfig(day);
        const inscritos = 0;
        const quorum = Number(cfg.quorumMinimo) || 0;
        const valorGuia = Number(cfg.valorGuia) || 0;
        const horarioSaida = `${cfg.horaSaida}:${cfg.minutoSaida}`;
        const rowsValidas = cfg.hasTransport
          ? cfg.transportRows.filter((r) => r.seatsAvailable && r.valorIndividual && r.quorum)
          : [];
        const vagasComTransporte = rowsValidas.reduce((acc, r) => acc + (Number(r.seatsAvailable) || 0), 0);
        const hasTransport = cfg.hasTransport && rowsValidas.length > 0 && vagasComTransporte > 0;
        const valorVagaTransporte = rowsValidas.length > 0
          ? Math.trunc(Number(rowsValidas[0].valorIndividual) || 0)
          : 0;
        const quorumMinimoTransporte = rowsValidas.reduce((acc, r) => acc + (Number(r.quorum) || 0), 0);

        const mappedExperiences = cfg.experiences
          .map((id) => experiencias.findIndex((e) => e.id === id) + 1)
          .filter((n) => n > 0);
        const selectedExperienceTitles = cfg.experiences
          .map((id) => experiencias.find((e) => e.id === id)?.titulo?.trim())
          .filter((v): v is string => Boolean(v));
        const passeioTitle =
          selectedExperienceTitles.length > 0 ? selectedExperienceTitles.join(' + ') : `Excursão ${cfg.city}`;

        const ex: Excursao = {
          id: `admin-excursao-${Date.now()}-${Math.random().toString(36).slice(2, 7)}-${day}`,
          date: day,
          status: 'em formação',
          city: cfg.city,
          maxPessoas: Number(cfg.maxPessoas) || 0,
          quorumMinimo: quorum,
          valorPorPessoa: Number(cfg.valorPorPessoa) || 0,
          valorGuia,
          inscritos,
          horarioSaida: horarioSaida || '09:00',
          pontoPartida: cfg.pontoPartida || '',
          gps: cfg.gps || '',
          experiences: mappedExperiences,
          experienceAdminIds: cfg.experiences.length > 0 ? [...cfg.experiences] : undefined,
          hasTransport,
          vagasComTransporte: hasTransport ? vagasComTransporte : undefined,
          valorVagaTransporte: hasTransport ? valorVagaTransporte : undefined,
          quorumMinimoTransporte: hasTransport ? quorumMinimoTransporte : undefined,
          guiaId: cfg.guiaId || undefined,
          guiaEmail: cfg.guiaEmail || undefined,
          clienteIds: [],
          criadaPor: 'admin',
          title: passeioTitle,
          subtitle: JSON.stringify({ valorGuia }),
          createdAt: new Date().toISOString(),
        };
        addExcursaoToStorage(ex);
        return ex;
      });

      toSync.forEach((exc) => {
        const titulo = (exc.title || 'Excursão').trim();
        const nomeExperiencias = titulo || 'Excursão';
        const dataBr = new Date(`${exc.date}T12:00:00`).toLocaleDateString('pt-BR');
        const vagas = Number(exc.maxPessoas || 0);
        const pagamentoGuia = Number(exc.valorGuia || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        addNotification({
          toEmail: 'guia@testando.com',
          type: 'info',
          title: `Nova Excursão Disponível (${titulo})`,
          description: `${dataBr} ${nomeExperiencias} saindo de ${exc.city} ${exc.horarioSaida} Para até ${vagas} Pagamento: R$${pagamentoGuia} por inscrito`,
          metadata: { source: 'admin', excursionId: exc.id, date: exc.date },
        });
      });

      try {
        const allShared = Object.values(getSharedExcursoesFromStorage());
        await fetch(apiUrl('/api/excursoes/sync?source=admin'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(allShared),
        });
      } catch {
        // sync best-effort
      }

      window.dispatchEvent(new Event('excursao-updated'));
      setShowSuccessModal(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout>
      <div className="w-full">
        <div className="sticky top-0 z-30 px-6 py-4 border-b border-gray-200 bg-white/95 backdrop-blur shadow-sm">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <nav className="text-sm text-gray-500 mb-1">
                <Link to="/dashboard" className="hover:text-gray-700">Home</Link>
                {' > '}
                <Link to="/excursoes" className="hover:text-gray-700">Excursão</Link>
                {' > '}
                <span className="text-gray-800">{isEditMode ? 'Editar Excursão' : 'Nova Excursão'}</span>
              </nav>
              <h1 className="text-2xl font-bold text-gray-900">{isEditMode ? 'Editar Excursão' : 'Nova Excursão'}</h1>
            </div>
            <div className="flex items-center gap-3 ml-auto">
              <Link to="/excursoes" className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">
                Cancelar
              </Link>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || periodoDias.length === 0}
                className="px-5 py-2 rounded-lg bg-orange-500 text-white font-medium hover:bg-orange-600 disabled:opacity-60"
              >
                {saving ? 'Salvando...' : (isEditMode ? 'Salvar alterações' : 'Criar excursão')}
              </button>
            </div>
          </div>
        </div>

        <div className="px-6 py-6 space-y-6">
          {/* Abas: Embarque + dias */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="flex gap-2 p-4 pb-2 overflow-x-auto flex-wrap border-b border-gray-200">
              <button
                type="button"
                onClick={() => setActiveMainTab('embarque')}
                className={`px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap shrink-0 transition-colors ${
                  activeMainTab === 'embarque'
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Embarque
              </button>
              {periodoDias.map((day) => {
                const cfg = ensureConfig(day);
                const experienciasDoDia = cfg.experiences
                  .map((id) => experienciasById[id])
                  .filter((e): e is ExperienciaAdmin => Boolean(e));
                const isActive = activeMainTab === day;
                const numAtividades = experienciasDoDia.length;
                const soData = numAtividades === 0;
                return (
                  <button
                    key={day}
                    type="button"
                    onClick={() => {
                      setActiveMainTab(day);
                      setSelectedDayTab(day);
                    }}
                    className={`px-3 py-2.5 rounded-lg text-sm font-medium text-left shrink-0 transition-colors flex flex-col justify-center min-h-[72px] ${
                      numAtividades >= 3 ? 'min-w-[260px] w-[260px]' : 'min-w-[180px] w-[180px]'
                    } ${isActive ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                  >
                    <div className={`flex flex-col justify-center h-full w-full overflow-hidden ${soData ? 'items-center' : 'items-start'}`}>
                      <div className={`font-semibold w-full truncate text-left ${soData ? 'text-lg' : 'text-xs'} ${isActive ? 'text-white' : 'text-gray-700'}`}>
                        {formatDateDDMMAA(day)}
                      </div>
                      {numAtividades > 0 ? (
                        <div className={`font-normal w-full overflow-hidden mt-0.5 ${numAtividades <= 2 ? 'text-xs' : 'text-[11px]'} ${isActive ? 'text-white/90' : 'text-gray-500'}`}>
                          {experienciasDoDia.map((exp) => (
                            <div key={exp.id} className="truncate leading-tight" title={`${exp.titulo}${exp.subtitulo ? ` - ${exp.subtitulo}` : ''}`}>
                              <span className="truncate block">{exp.titulo}</span>
                              {exp.subtitulo && (
                                <span className={`truncate block ${isActive ? 'text-white/75' : 'text-gray-400'}`}>{exp.subtitulo}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  </button>
                );
              })}
            </div>

            {activeMainTab === 'embarque' && (
            <div className="p-5">
            <section className="space-y-4">
            <button
              type="button"
              onClick={() => {
                if (!showDatePicker) {
                  setSelectingStartDate(true);
                  setCurrentMonth(new Date());
                }
                setShowDatePicker((v) => !v);
              }}
              className="w-full bg-white rounded-2xl px-5 py-4 flex items-center justify-between border border-gray-200 hover:shadow-sm transition-all"
            >
              <div className="flex items-center gap-3">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <path d="M19 4H5C3.89543 4 3 4.89543 3 6V20C3 21.1046 3.89543 22 5 22H19C20.1046 22 21 21.1046 21 20V6C21 4.89543 20.1046 4 19 4Z" stroke="currentColor" strokeWidth="2" className="text-gray-600" />
                  <path d="M16 2V6M8 2V6M3 10H21" stroke="currentColor" strokeWidth="2" className="text-gray-600" />
                </svg>
                <div className="text-left">
                  <p className="text-xs text-gray-500 mb-1">Qual o período?</p>
                  <span className={startDate && endDate ? 'text-gray-800 font-medium text-sm' : 'text-gray-400 text-sm'}>
                    {startDate && endDate
                      ? startDate === endDate
                        ? `${formatDate(startDate)} (1 dia)`
                        : `${formatDate(startDate)} - ${formatDate(endDate)}`
                      : 'Selecione a data'}
                  </span>
                </div>
              </div>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className={`transform transition-transform ${showDatePicker ? 'rotate-180' : ''}`}>
                <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            {showDatePicker && (
              <div>
                <p className="mt-3 text-xs text-gray-500">
                  {selectingStartDate ? 'Selecione a data inicial' : 'Selecione a data final'}
                </p>
                {renderCalendar()}
              </div>
            )}
            <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 p-4">
              <h3 className="text-sm font-semibold text-gray-800 mb-3">Embarque</h3>
              {(() => {
                const primaryCfg = periodoDias.length > 0 ? ensureConfig(periodoDias[0]) : getDefaultDayConfig();
                const bloqueiaCidadeGlobal = isEditMode && Number(editExcursao?.inscritos || 0) > 0;
                return (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                    <label className="text-sm text-gray-700">
                      Cidade
                      <select
                        value={primaryCfg.city}
                        onChange={(e) => applyGlobalFieldsToAllDays({ city: e.target.value })}
                        disabled={bloqueiaCidadeGlobal}
                        className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                      >
                        {cidades.map((c) => (
                          <option key={c.id} value={c.cidade}>{c.cidade}</option>
                        ))}
                      </select>
                    </label>
                    <div className="text-sm text-gray-700">
                      Horário de saída
                      <div className="mt-1 grid grid-cols-2 gap-2">
                        <select
                          value={primaryCfg.horaSaida}
                          onChange={(e) => applyGlobalFieldsToAllDays({ horaSaida: e.target.value })}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2"
                        >
                          {horas.map((h) => (
                            <option key={h} value={h}>{h}h</option>
                          ))}
                        </select>
                        <select
                          value={primaryCfg.minutoSaida}
                          onChange={(e) => applyGlobalFieldsToAllDays({ minutoSaida: e.target.value as DayConfig['minutoSaida'] })}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2"
                        >
                          {minutosValidos.map((m) => (
                            <option key={m} value={m}>{m} min</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="text-sm text-gray-700 md:col-span-2 lg:col-span-2">
                      <div className="flex items-center gap-2 mb-1">
                        <span>Ponto de partida e GPS</span>
                        <button
                          type="button"
                          onClick={() => setLocationMode('address')}
                          className={`px-2.5 py-1 rounded-full text-xs border ${locationMode === 'address' ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-gray-600 border-gray-300'}`}
                        >
                          Endereço
                        </button>
                        <button
                          type="button"
                          onClick={() => setLocationMode('gps')}
                          className={`px-2.5 py-1 rounded-full text-xs border ${locationMode === 'gps' ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-gray-600 border-gray-300'}`}
                        >
                          Link/GPS
                        </button>
                      </div>
                      {locationMode === 'address' ? (
                        <input
                          ref={addressInputRef}
                          type="text"
                          value={globalPeriodConfig.pontoPartida}
                          onChange={(e) => applyGlobalFieldsToAllDays({ pontoPartida: e.target.value })}
                          onBlur={() => {
                            const addr = globalPeriodConfig.pontoPartida?.trim();
                            if (addr) geocodeAddressToMap(addr);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              const addr = globalPeriodConfig.pontoPartida?.trim();
                              if (addr) geocodeAddressToMap(addr);
                            }
                          }}
                          placeholder="Digite e selecione o endereço (Google)"
                          className="w-full rounded-lg border border-gray-300 px-3 py-2"
                        />
                      ) : (
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={globalPeriodConfig.gps}
                            onChange={(e) => applyGlobalFieldsToAllDays({ gps: e.target.value })}
                            placeholder="Cole link do Google Maps ou lat,lng"
                            className="w-full rounded-lg border border-gray-300 px-3 py-2"
                          />
                          <button
                            type="button"
                            onClick={handleApplyGpsInput}
                            className="px-3 py-2 rounded-lg border border-orange-500 text-orange-600 hover:bg-orange-50 text-sm"
                          >
                            Aplicar
                          </button>
                        </div>
                      )}
                      {!googleMapsApiKey && (
                        <p className="mt-1 text-xs text-amber-700">
                          Configure `VITE_GOOGLE_MAPS_API_KEY` para habilitar autocomplete e mapa.
                        </p>
                      )}
                      {mapsError && (
                        <p className="mt-1 text-xs text-red-600">{mapsError}</p>
                      )}
                      <div className="mt-2 w-full h-44 rounded-lg border border-gray-300 overflow-hidden bg-gray-100">
                        {locationCoords && mapsReady ? (
                          <div ref={mapContainerRef} className="w-full h-full" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-xs text-gray-500 px-3 text-center">
                            Selecione um endereço no autocomplete ou cole o GPS para visualizar no mapa.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
            <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
              <div className="text-sm text-gray-600">
                {periodoDias.length > 0 ? `${periodoDias.length} dia(s) selecionado(s)` : ''}
              </div>
            </div>
            </section>
            </div>
            )}

          {periodoDias.some((d) => activeMainTab === d) && (
          <div className="p-5">
          {periodoDias.filter((day) => activeMainTab === day).map((day) => {
            const cfg = ensureConfig(day);
            const isEditingDay = isEditMode && editExcursao?.date === day;
            const inscritosAtuais = isEditingDay ? Number(editExcursao?.inscritos || 0) : 0;
            const inscritosTransporteAtuais = isEditingDay ? Number(editExcursao?.inscritosComTransporte || 0) : 0;
            const bloqueiaCamposNaoPermitidos = isEditingDay && inscritosAtuais > 0;
            const grupoValorConfigurado = Boolean(cfg.maxPessoas && cfg.valorPorPessoa && cfg.quorumMinimo);
            const vagasGrupoNum = Number(cfg.maxPessoas);
            const limiteMaximoPorAtividade = getLimiteMaximoPorAtividades(cfg.experiences);
            const diariaMinima = getDiariaMinimaTarifario(cfg.experiences, cfg.city);
            const valorPessoa = Number(cfg.valorPorPessoa) || 0;
            const quorumMinParaVagas = diariaMinima != null && valorPessoa > 0 && limiteMaximoPorAtividade != null && limiteMaximoPorAtividade > 0
              ? getQuorumMinimoCalculado(diariaMinima, valorPessoa, limiteMaximoPorAtividade)
              : 1;
            let vagasMin = quorumMinParaVagas;
            if (bloqueiaCamposNaoPermitidos && inscritosAtuais > vagasMin) {
              vagasMin = inscritosAtuais;
            }
            const vagasOptions = limiteMaximoPorAtividade != null && limiteMaximoPorAtividade > 0 && limiteMaximoPorAtividade >= vagasMin
              ? Array.from(
                  { length: Math.max(0, limiteMaximoPorAtividade - vagasMin + 1) },
                  (_, i) => String(i + vagasMin)
                )
              : [];
            let quorumMinCalculado = diariaMinima != null && valorPessoa > 0 && Number.isFinite(vagasGrupoNum) && vagasGrupoNum > 0
              ? getQuorumMinimoCalculado(diariaMinima, valorPessoa, vagasGrupoNum)
              : 1;
            if (bloqueiaCamposNaoPermitidos && inscritosAtuais > quorumMinCalculado) {
              quorumMinCalculado = inscritosAtuais;
            }
            const quorumOptions = vagasOptions;

            return (
              <section key={day} className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-base font-semibold text-gray-700">
                    {formatDateDDMMAA(day)} Definição do grupo
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
                  <label className="text-sm text-gray-700">
                    Vagas (grupo)
                    <select
                      value={cfg.maxPessoas}
                      onChange={(e) => {
                        const vagas = e.target.value;
                        const patch: Partial<DayConfig> = { maxPessoas: vagas };
                        if (vagas) {
                          const vagasNum = Number(vagas);
                          const quorumNum = Number(cfg.quorumMinimo) || 0;
                          if (vagasNum < quorumNum) patch.quorumMinimo = vagas;
                        }
                        updateConfig(day, patch);
                      }}
                      disabled={vagasOptions.length === 0}
                      className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 disabled:bg-gray-100"
                    >
                      {vagasOptions.length === 0 ? (
                        <option value="" disabled>Selecione atividades</option>
                      ) : null}
                      {vagasOptions.map((q) => (
                        <option key={q} value={q}>{q}</option>
                      ))}
                    </select>
                    {isEditingDay && inscritosAtuais > 0 && (
                      <p className="mt-1 text-xs text-gray-500">Inscritos atuais: {inscritosAtuais}</p>
                    )}
                  </label>
                  <label className="text-sm text-gray-700">
                    Quórum
                    <select
                      value={!cfg.maxPessoas ? (quorumOptions[0] || '') : (quorumOptions.includes(cfg.quorumMinimo) && Number(cfg.quorumMinimo) <= Number(cfg.maxPessoas) ? cfg.quorumMinimo : cfg.maxPessoas)}
                      disabled={!cfg.maxPessoas || quorumOptions.length === 0}
                      onChange={(e) => {
                        const valor = e.target.value;
                        const vagasNum = Number(cfg.maxPessoas) || 0;
                        const quorumNum = Number(valor) || 0;
                        const quorumFinal = quorumNum > vagasNum ? cfg.maxPessoas : valor;
                        updateConfig(day, { quorumMinimo: quorumFinal });
                      }}
                      className={`mt-1 w-full rounded-lg border px-3 py-2 [&_option:disabled]:opacity-20 [&_option:disabled]:bg-gray-200 ${cfg.maxPessoas && cfg.quorumMinimo && Number(cfg.quorumMinimo) > Number(cfg.maxPessoas) ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                    >
                      {quorumOptions.map((q) => {
                        const vagasNum = Number(cfg.maxPessoas) || 0;
                        const qNum = Number(q);
                        const bloqueado = vagasNum > 0 && qNum > vagasNum;
                        return (
                          <option key={q} value={q} disabled={bloqueado}>{q}</option>
                        );
                      })}
                    </select>
                    {diariaMinima != null && valorPessoa > 0 && (
                      <p className="mt-1 text-xs">
                        <span className="text-red-600">Mínimo </span>
                        <span className="text-blue-600 font-semibold">{quorumMinCalculado} pessoa{quorumMinCalculado !== 1 ? 's' : ''}</span>
                        <span className="text-red-600"> para total de R$ </span>
                        <span className="text-blue-600 font-semibold">{(quorumMinCalculado * valorPessoa).toLocaleString('pt-BR')}</span>
                      </p>
                    )}
                    {cfg.maxPessoas && cfg.quorumMinimo && Number(cfg.quorumMinimo) > Number(cfg.maxPessoas) && (
                      <p className="mt-1 text-xs text-red-600 font-semibold">
                        ERRO: Quórum de pessoas deve ser menor ou igual ao número de vagas no grupo
                      </p>
                    )}
                  </label>
                  <label className="text-sm text-gray-700">
                    Valor PAX (R$)
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={cfg.valorPorPessoa}
                      onChange={(e) => {
                        const numeric = e.target.value.replace(/\D/g, '');
                        const valorNum = Number(numeric);
                        const taxaNum = Number(cfg.taxaAplicada) || 0;
                        const patch: Partial<DayConfig> = {
                          valorPorPessoa: numeric,
                          valorGuia: String(calcularRecebimentoGuia(valorNum, taxaNum)),
                        };
                        const limiteMax = getLimiteMaximoPorAtividades(cfg.experiences);
                        const maxPessoas = Number(cfg.maxPessoas) || 0;
                        const diariaMin = getDiariaMinimaTarifario(cfg.experiences, cfg.city);
                        const valorMinimo = getValorMinimoPorPessoaExcursao(diariaMin ?? 0, limiteMax ?? maxPessoas);
                        // Só recalcular vagas/quórum quando valor >= mínimo; valor abaixo do mínimo desconfigura os combos
                        const valorValidoParaRecalc = valorNum <= 0 || (valorMinimo != null && valorNum < valorMinimo)
                          ? false
                          : true;
                        if (valorValidoParaRecalc && limiteMax != null && limiteMax > 0 && diariaMin != null && valorNum > 0) {
                          const vagasMinCalculado = getQuorumMinimoCalculado(diariaMin, valorNum, limiteMax);
                          const vagasMinNovo = bloqueiaCamposNaoPermitidos && inscritosAtuais > vagasMinCalculado
                            ? inscritosAtuais
                            : vagasMinCalculado;
                          const vagasAtual = Number(cfg.maxPessoas) || 0;
                          const vagasValido = vagasAtual >= vagasMinNovo && vagasAtual <= limiteMax;
                          const vagasNovo = !vagasValido || !cfg.maxPessoas
                            ? String(Math.min(Math.max(vagasMinNovo, 1), limiteMax))
                            : cfg.maxPessoas;
                          patch.maxPessoas = vagasNovo;
                          const vagasNovoNum = Number(vagasNovo) || 0;
                          // Sempre atualizar quórum para o menor valor possível para este valor
                          patch.quorumMinimo = String(Math.min(vagasMinNovo, vagasNovoNum));
                        }
                        updateConfig(day, patch);
                      }}
                      onBlur={async () => {
                        const valorNum = Number(cfg.valorPorPessoa) || 0;
                        if (valorNum <= 0) return;
                        const maxPessoas = Number(cfg.maxPessoas) || 0;
                        const diariaMin = getDiariaMinimaTarifario(cfg.experiences, cfg.city);
                        const valorMinimo = getValorMinimoPorPessoaExcursao(diariaMin ?? 0, maxPessoas);
                        if (valorMinimo != null && valorNum < valorMinimo) {
                          await showAlert(
                            `Valor mínimo permitido para excursão é de R$ ${valorMinimo.toLocaleString('pt-BR')}`,
                            'Aviso',
                            'Entendi'
                          );
                          const taxaNum = Number(cfg.taxaAplicada) || 0;
                          updateConfig(day, {
                            valorPorPessoa: String(valorMinimo),
                            valorGuia: String(calcularRecebimentoGuia(valorMinimo, taxaNum)),
                          });
                        }
                      }}
                      className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-blue-600 font-semibold"
                    />
                    {(() => {
                      const sugerido = getSugeridoExcursaoIndividualBrl(cfg.experiences, cfg.city);
                      const diariaMin = getDiariaMinimaTarifario(cfg.experiences, cfg.city);
                      const maxPessoas = Number(cfg.maxPessoas) || 0;
                      const valorMinimoInfo = getValorMinimoPorPessoaExcursao(diariaMin ?? 0, maxPessoas);
                      return sugerido != null || diariaMin != null || valorMinimoInfo != null ? (
                        <p className="mt-1 text-xs">
                          {sugerido != null && (
                            <>
                              <span className="text-red-600">Preço sugerido R$ </span>
                              <span className="text-blue-600 font-semibold">{sugerido.toLocaleString('pt-BR')}</span>
                            </>
                          )}
                          {sugerido != null && (diariaMin != null || valorMinimoInfo != null) && <span className="text-red-600"> e </span>}
                          {diariaMin != null && (
                            <>
                              <span className="text-red-600">Diária Mínima R$ </span>
                              <span className="text-blue-600 font-semibold">{diariaMin.toLocaleString('pt-BR')}</span>
                            </>
                          )}
                          {diariaMin != null && valorMinimoInfo != null && <span className="text-red-600"> e </span>}
                          {valorMinimoInfo != null && (
                            <>
                              <span className="text-red-600">Valor Mínimo R$ </span>
                              <span className="text-blue-600 font-semibold">{valorMinimoInfo.toLocaleString('pt-BR')}</span>
                            </>
                          )}
                        </p>
                      ) : null;
                    })()}
                  </label>
                  <label className="text-sm text-gray-700">
                    Taxa aplicada (%)
                    <input
                      type="text"
                      inputMode="decimal"
                      value={cfg.taxaAplicada}
                      onChange={(e) => {
                        const v = e.target.value.replace(/[^\d.,]/g, '').replace(',', '.');
                        const taxaNum = Number(v) || 0;
                        const valorNum = Number(cfg.valorPorPessoa) || 0;
                        updateConfig(day, {
                          taxaAplicada: v,
                          valorGuia: String(calcularRecebimentoGuia(valorNum, taxaNum)),
                        });
                      }}
                      placeholder="Tarifário Taxa Agência"
                      className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                    />
                  </label>
                  <label className="text-sm text-gray-700">
                    Recebimento Guia / por PAX BRL (R$)
                    <div className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 bg-gray-50 text-blue-600 font-semibold min-h-[42px] flex items-center">
                      {cfg.valorGuia || '—'}
                    </div>
                  </label>
                </div>

                <div className="text-sm text-gray-700">
                  Atividades do dia
                  <div className="mt-1 rounded-lg border border-gray-300 p-3 max-h-40 overflow-y-auto grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2 content-start">
                    {(() => {
                      const todasNaCidade = getExperienciasDisponiveisNaCidade(cfg.city);
                      const principaisNaCidade = todasNaCidade.filter((e) => !e.naoEhAtividadePrincipal);
                      const disponiveisIds = new Set(todasNaCidade.map((exp) => exp.id));
                      const allowed = getAllowedExperienceIds(cfg.experiences, disponiveisIds);
                      const experienciasDisponiveis =
                        cfg.experiences.length === 0
                          ? principaisNaCidade
                          : todasNaCidade.filter((e) => allowed.has(e.id));
                      if (experienciasDisponiveis.length === 0) {
                        return (
                          <p className="text-xs text-gray-500">
                            Nenhuma atividade com embarque em {cfg.city}.
                          </p>
                        );
                      }
                      return experienciasDisponiveis.map((exp) => {
                      const checked = cfg.experiences.includes(exp.id);
                      const disabled = !checked && cfg.experiences.length > 0 && !allowed.has(exp.id);
                      const selectedCount = getSelectedCount(exp.id);
                      return (
                        <label key={exp.id} className={`flex items-start gap-2 rounded-md px-1.5 py-1 ${disabled ? 'opacity-50' : ''}`}>
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={disabled}
                            onChange={() => toggleExperience(day, exp.id)}
                            className="mt-0.5"
                          />
                          <span className="text-sm text-gray-800">
                            {exp.titulo} ({selectedCount})
                          </span>
                        </label>
                      );
                      });
                    })()}
                  </div>
                </div>

                <div className="rounded-xl border-2 border-dashed border-sky-300 bg-sky-50/50 p-4 space-y-3">
                  <label className="inline-flex items-center gap-2 text-sm font-semibold text-gray-800">
                    <input
                      type="checkbox"
                      checked={cfg.hasTransport}
                      disabled={isEditingDay && inscritosTransporteAtuais > 0}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        const current = ensureConfig(day);
                        if (checked && current.transportRows.length === 0) {
                          const valorTransladoAuto = calcularValorIndividualTranslado(current.experiences, current.city);
                          updateConfig(day, {
                            hasTransport: checked,
                            transportRows: [createTransportRow(valorTransladoAuto != null ? String(valorTransladoAuto) : '')],
                          });
                          return;
                        }
                        updateConfig(day, { hasTransport: checked });
                      }}
                    />
                    Opção com translado (opcional)
                  </label>
                  <p className="text-xs text-gray-600">
                    Máximo 3 veículos e 12 assentos no total. Taxa aplicada vem do Tarifário Desconto Veículo Compartilhado (%).
                  </p>
                  {cfg.hasTransport && (
                    <div className="space-y-3">
                      {isEditingDay && inscritosTransporteAtuais > 0 && (
                        <p className="text-xs text-sky-800 font-medium">
                          Vagas com transporte já compradas: {inscritosTransporteAtuais}
                        </p>
                      )}
                      {cfg.transportRows.map((row, idx) => {
                        const vehicleType = normalizeVehicleType(row.vehicleSize);
                        const maxSeats = getMaxSeatsForVehicle(vehicleType);
                        const outrosAssentos = cfg.transportRows
                          .filter((_, i) => i !== idx)
                          .reduce((a, r) => a + (Number(r.seatsAvailable) || 0), 0);
                        const maxPermitido = Math.min(maxSeats, 12 - outrosAssentos);
                        const seatsAvailable = Math.min(maxPermitido, Math.max(1, Number(row.seatsAvailable) || 1));
                        const seatOptions = Array.from({ length: maxPermitido }, (_, i) => String(i + 1));
                        const quorumOptions = Array.from({ length: maxPermitido }, (_, i) => String(i + 1));
                        const canRemove = cfg.transportRows.length >= 2 && idx > 0;
                        return (
                          <div key={row.id} className="rounded-lg border border-sky-200 bg-white p-3">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-3">
                              <label className="text-sm text-gray-700">
                                Veículo
                                <select
                                  value={vehicleType}
                                  onChange={(e) => {
                                    const v = e.target.value as VehicleType;
                                    const opts = getVehicleOptionsForCity(cfg.city);
                                    const opt = opts.find((o) => o.value === v);
                                    if (opt?.blocked) return;
                                    updateTransportRow(day, row.id, { vehicleSize: v });
                                  }}
                                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 [&_option:disabled]:opacity-50 [&_option:disabled]:bg-slate-200"
                                >
                                  {getVehicleOptionsForCity(cfg.city).map((o) => (
                                    <option key={o.value} value={o.value} disabled={o.blocked}>{o.label}</option>
                                  ))}
                                </select>
                              </label>
                              <label className="text-sm text-gray-700">
                                Assentos disponíveis
                                <select
                                  value={row.seatsAvailable}
                                  onChange={(e) => updateTransportRow(day, row.id, { seatsAvailable: e.target.value })}
                                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                                >
                                  {seatOptions.map((s) => (
                                    <option key={s} value={s}>{s}</option>
                                  ))}
                                </select>
                              </label>
                              <label className="text-sm text-gray-700">
                                Quórum
                                <select
                                  value={quorumOptions.includes(row.quorum) ? row.quorum : String(seatsAvailable)}
                                  onChange={(e) => {
                                    const valor = e.target.value;
                                    const assentosNum = Number(row.seatsAvailable) || 0;
                                    const quorumNum = Number(valor) || 0;
                                    const quorumFinal = quorumNum > assentosNum ? row.seatsAvailable : valor;
                                    updateTransportRow(day, row.id, { quorum: quorumFinal });
                                  }}
                                  className={`mt-1 w-full rounded-lg border px-3 py-2 [&_option:disabled]:opacity-20 [&_option:disabled]:bg-gray-200 ${row.seatsAvailable && row.quorum && Number(row.quorum) > Number(row.seatsAvailable) ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                                >
                                  {quorumOptions.map((q) => {
                                    const assentosNum = Number(row.seatsAvailable) || 0;
                                    const qNum = Number(q);
                                    const bloqueado = assentosNum > 0 && qNum > assentosNum;
                                    return (
                                      <option key={q} value={q} disabled={bloqueado}>{q}</option>
                                    );
                                  })}
                                </select>
                                {row.seatsAvailable && row.quorum && Number(row.quorum) > Number(row.seatsAvailable) && (
                                  <p className="mt-1 text-xs text-red-600 font-semibold">
                                    ERRO: Quórum deve ser menor ou igual ao número de assentos disponíveis
                                  </p>
                                )}
                              </label>
                              <label className="text-sm text-gray-700">
                                Valor Individual (R$)
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  pattern="[0-9]*"
                                  value={row.valorIndividual}
                                  onChange={(e) => {
                                    const numeric = e.target.value.replace(/\D/g, '');
                                    updateTransportRow(day, row.id, { valorIndividual: numeric });
                                  }}
                                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                                />
                              </label>
                              <label className="text-sm text-gray-700">
                                Taxa aplicada (%)
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  value={row.taxaAplicada}
                                  onChange={(e) => {
                                    const v = e.target.value.replace(/[^\d.,]/g, '').replace(',', '.');
                                    updateTransportRow(day, row.id, { taxaAplicada: v });
                                  }}
                                  placeholder="Tarifário"
                                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                                />
                              </label>
                              <label className="text-sm text-gray-700 whitespace-nowrap">
                                Recebimento Guia / por PAX BRL (R$)
                                <div className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 bg-gray-50 text-blue-600 font-semibold min-h-[42px] flex items-center">
                                  {row.valorGuia || '—'}
                                </div>
                              </label>
                              <div className="text-sm text-gray-700 flex items-end">
                                {canRemove ? (
                                  <button
                                    type="button"
                                    onClick={() => removeTransportRow(day, row.id)}
                                    className="w-full rounded-lg border border-red-300 text-red-700 px-3 py-2 hover:bg-red-50"
                                  >
                                    Remover veículo
                                  </button>
                                ) : (
                                  <div className="w-full" />
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      <button
                        type="button"
                        onClick={() => addTransportRow(day)}
                        disabled={cfg.transportRows.length >= 3 || cfg.transportRows.reduce((a, r) => a + (Number(r.seatsAvailable) || 0), 0) >= 12}
                        className="rounded-lg border border-sky-300 text-sky-800 px-3 py-2 text-sm font-medium hover:bg-sky-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        + Adicionar veículo
                      </button>
                      <p className="text-xs text-sky-800 font-medium">
                        Total de assentos com translado: {cfg.transportRows.reduce((acc, r) => acc + (Number(r.seatsAvailable) || 0), 0)}
                      </p>
                    </div>
                  )}
                </div>

                <div
                  className={`rounded-xl border-2 border-dashed p-4 ${
                    grupoValorConfigurado ? 'border-orange-300 bg-orange-50/50' : 'border-gray-300 bg-gray-50 opacity-70'
                  }`}
                >
                  <p className="text-sm font-semibold text-gray-800">Definir Guia (opcional)</p>
                  <p className="text-xs text-gray-600 mb-3">
                    Este bloco só libera após configurar grupo e valor do dia.
                  </p>
                  <select
                    disabled={!grupoValorConfigurado}
                    value={cfg.guiaId}
                    onChange={(e) => {
                      const guia = guias.find((g) => g.id === e.target.value);
                      updateConfig(day, {
                        guiaId: guia?.id || '',
                        guiaEmail: guia?.email || '',
                      });
                    }}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 disabled:bg-gray-100"
                  >
                    <option value="">Indefinido</option>
                    {guias.map((g) => (
                      <option key={g.id} value={g.id}>{g.nome} ({g.email})</option>
                    ))}
                  </select>
                  <label className="mt-3 inline-flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      disabled={!grupoValorConfigurado}
                      checked={cfg.showGuia}
                      onChange={(e) => updateConfig(day, { showGuia: e.target.checked })}
                    />
                    Mostrar guia
                  </label>
                </div>
              </section>
            );
          })}
          </div>
          )}
          </div>
        </div>
      </div>
      {showSuccessModal && (
        <div
          className="fixed inset-0 z-[300] bg-black/40 flex items-center justify-center p-4"
          onClick={() => {
            setShowSuccessModal(false);
            navigate('/excursoes', { replace: true });
          }}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white shadow-xl border border-green-200 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 bg-green-50 border-b border-green-200 flex items-center gap-3">
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-green-100 text-green-700">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </span>
              <h3 className="text-lg font-semibold text-green-900">Sucesso</h3>
            </div>
            <div className="px-5 py-4">
              <p className="text-sm text-gray-700">
                {isEditMode ? 'Excursão atualizada com sucesso.' : 'Excursão criada com sucesso.'}
              </p>
            </div>
            <div className="px-5 pb-5 flex justify-end">
              <button
                type="button"
                onClick={() => {
                  setShowSuccessModal(false);
                  navigate('/excursoes', { replace: true });
                }}
                className="px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
